import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { UserPlus, Plus, Trash2, ToggleLeft, ToggleRight, MousePointer, TrendingUp, DollarSign, Clock, CreditCard, Loader2, History, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const AdminAffiliates = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [affiliates, setAffiliates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [payoutSchedule, setPayoutSchedule] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('affiliates');
  const [form, setForm] = useState({ name: '', email: '', referral_code: '', commission_rate: 10, notes: '' });
  const headers = { Authorization: `Bearer ${token}` };

  const fetch = async () => {
    try { const res = await axios.get(`${API}/admin/affiliates`, { headers }); setAffiliates(res.data); } catch {}
    try { const res = await axios.get(`${API}/admin/affiliate-analytics`, { headers }); setAnalytics(res.data); } catch {}
    try { const res = await axios.get(`${API}/connect/payout-schedule`, { headers }); setPayoutSchedule(res.data); } catch {}
    try { const res = await axios.get(`${API}/connect/affiliate-payouts`, { headers }); setPayoutHistory(res.data); } catch {}
  };
  useEffect(() => { fetch(); }, []);

  const create = async () => {
    if (!form.name || !form.referral_code) { toast({ title: 'Name and referral code required', variant: 'destructive' }); return; }
    try { await axios.post(`${API}/admin/affiliates`, form, { headers }); toast({ title: 'Affiliate created' }); setShowForm(false); setForm({ name: '', email: '', referral_code: '', commission_rate: 10, notes: '' }); fetch(); }
    catch (err) { toast({ title: 'Error', description: err.response?.data?.detail, variant: 'destructive' }); }
  };

  const toggle = async (id, active) => { await axios.put(`${API}/admin/affiliates/${id}`, { is_active: !active }, { headers }); fetch(); };
  const remove = async (id) => { if (!window.confirm('Delete affiliate?')) return; await axios.delete(`${API}/admin/affiliates/${id}`, { headers }); toast({ title: 'Deleted' }); fetch(); };

  const triggerPayout = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/connect/trigger-affiliate-payouts`, {}, { headers });
      toast({ title: 'Payouts processed', description: res.data.message });
      fetch();
    } catch (err) {
      toast({ title: 'Payout failed', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setTriggering(false); }
  };

  const statusIcon = (status) => {
    if (status === 'completed') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />;
    if (status === 'failed') return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    return <AlertCircle className="w-3.5 h-3.5 text-amber-400" />;
  };

  const statusCls = (status) => {
    if (status === 'completed') return 'bg-emerald-500/10 text-emerald-400';
    if (status === 'failed') return 'bg-red-500/10 text-red-400';
    return 'bg-amber-500/10 text-amber-400';
  };

  return (
    <div data-testid="admin-affiliates-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-white">Affiliates</h1><p className="text-gray-500 text-sm">Manage referral partners and commissions</p></div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-affiliate-btn"><Plus className="w-4 h-4 mr-2" />New Affiliate</Button>
      </div>

      {/* Payout Schedule Card */}
      <Card className="bg-[#132218] border-emerald-900/30 mb-6" data-testid="payout-schedule-section">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Bi-Weekly Payout Schedule</p>
                <div className="flex items-center gap-4 mt-0.5">
                  <span className="text-xs text-gray-500">
                    Next payout: <span className="text-emerald-400">{payoutSchedule?.next_payout_date ? new Date(payoutSchedule.next_payout_date).toLocaleDateString() : 'Calculating...'}</span>
                  </span>
                  <span className="text-xs text-gray-500">
                    Pending: <span className="text-amber-400 font-medium">${payoutSchedule?.pending_total?.toFixed(2) || '0.00'}</span>
                  </span>
                  <span className="text-xs text-gray-500">
                    Affiliates due: <span className="text-white">{payoutSchedule?.pending_affiliates || 0}</span>
                  </span>
                </div>
              </div>
            </div>
            <Button onClick={triggerPayout} disabled={triggering} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="trigger-payout-btn">
              {triggering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Run Payouts Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="affiliate-analytics">
          <Card className="bg-[#132218] border-emerald-900/30"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-white">{analytics.total_affiliates}</p><p className="text-[10px] text-gray-500">Affiliates</p></CardContent></Card>
          <Card className="bg-[#132218] border-emerald-900/30"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-400">{analytics.total_clicks}</p><p className="text-[10px] text-gray-500">Total Clicks</p></CardContent></Card>
          <Card className="bg-[#132218] border-emerald-900/30"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-400">{analytics.total_referrals}</p><p className="text-[10px] text-gray-500">Referrals</p></CardContent></Card>
          <Card className="bg-[#132218] border-emerald-900/30"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-amber-400">{analytics.avg_conversion_rate}%</p><p className="text-[10px] text-gray-500">Conversion</p></CardContent></Card>
          <Card className="bg-[#132218] border-emerald-900/30"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-purple-400">${analytics.total_earnings.toFixed(2)}</p><p className="text-[10px] text-gray-500">Total Earnings</p></CardContent></Card>
        </div>
      )}

      {/* Tabs: Affiliates | Payout History */}
      <div className="flex gap-1 mb-6 bg-[#132218] rounded-lg p-1 w-fit" data-testid="affiliates-tabs">
        <button onClick={() => setActiveTab('affiliates')} data-testid="tab-affiliates"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'affiliates' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}>
          <UserPlus className="w-4 h-4" /> Affiliates
        </button>
        <button onClick={() => setActiveTab('payouts')} data-testid="tab-payouts"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'payouts' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}>
          <History className="w-4 h-4" /> Payout History
          {payoutHistory.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10">{payoutHistory.length}</span>}
        </button>
      </div>

      {/* Affiliates Tab */}
      {activeTab === 'affiliates' && (
        <>
          {showForm && (
            <Card className="bg-[#132218] border-emerald-900/30 mb-6" data-testid="affiliate-form">
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><Label className="text-gray-400 text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="affiliate-name-input" /></div>
                  <div><Label className="text-gray-400 text-xs">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="affiliate-email-input" /></div>
                  <div><Label className="text-gray-400 text-xs">Referral Code</Label><Input value={form.referral_code} onChange={e => setForm({...form, referral_code: e.target.value.toUpperCase()})} className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="affiliate-code-input" /></div>
                  <div><Label className="text-gray-400 text-xs">Commission %</Label><Input type="number" value={form.commission_rate} onChange={e => setForm({...form, commission_rate: parseFloat(e.target.value) || 0})} className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="affiliate-commission-input" /></div>
                </div>
                <div><Label className="text-gray-400 text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="bg-black/20 border-emerald-900/30 text-white mt-1" /></div>
                <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-affiliate-btn">Create Affiliate</Button>
              </CardContent>
            </Card>
          )}

          <Card className="bg-[#132218] border-emerald-900/30">
            <CardContent className="p-0">
              <table className="w-full">
                <thead><tr className="border-b border-emerald-900/30">
                  <th className="text-left text-xs text-gray-500 p-3">Affiliate</th>
                  <th className="text-left text-xs text-gray-500 p-3">Code</th>
                  <th className="text-left text-xs text-gray-500 p-3">Commission</th>
                  <th className="text-left text-xs text-gray-500 p-3">Referrals</th>
                  <th className="text-left text-xs text-gray-500 p-3">Earnings</th>
                  <th className="text-left text-xs text-gray-500 p-3">Status</th>
                  <th className="text-right text-xs text-gray-500 p-3">Actions</th>
                </tr></thead>
                <tbody>
                  {affiliates.map(a => (
                    <tr key={a.id} className="border-b border-emerald-900/10" data-testid={`affiliate-row-${a.id}`}>
                      <td className="p-3"><p className="text-white text-sm">{a.name}</p><p className="text-gray-500 text-xs">{a.email}</p></td>
                      <td className="p-3 font-mono text-emerald-400 text-sm font-bold">{a.referral_code}</td>
                      <td className="p-3 text-gray-400 text-sm">{a.commission_rate}%</td>
                      <td className="p-3 text-white text-sm">{a.total_referrals}</td>
                      <td className="p-3 text-emerald-400 text-sm">${a.total_earnings?.toFixed(2)}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${a.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{a.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td className="p-3 text-right flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => toggle(a.id, a.is_active)} className="text-gray-400">{a.is_active ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4" />}</Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(a.id)} className="text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                  {affiliates.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-8">No affiliates yet</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Payout History Tab */}
      {activeTab === 'payouts' && (
        <Card className="bg-[#132218] border-emerald-900/30" data-testid="payout-history-section">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <History className="w-4 h-4 text-emerald-400" /> Payout History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {payoutHistory.length > 0 ? (
              <table className="w-full" data-testid="payout-history-table">
                <thead><tr className="border-b border-emerald-900/30">
                  <th className="text-left text-xs text-gray-500 p-3">Date</th>
                  <th className="text-left text-xs text-gray-500 p-3">Affiliate</th>
                  <th className="text-left text-xs text-gray-500 p-3">Email</th>
                  <th className="text-right text-xs text-gray-500 p-3">Amount</th>
                  <th className="text-left text-xs text-gray-500 p-3">Status</th>
                  <th className="text-left text-xs text-gray-500 p-3">Stripe Transfer</th>
                </tr></thead>
                <tbody>
                  {payoutHistory.map(p => (
                    <tr key={p.id} className="border-b border-emerald-900/10" data-testid={`payout-row-${p.id}`}>
                      <td className="p-3 text-gray-300 text-sm">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                      <td className="p-3 text-white text-sm font-medium">{p.affiliate_name}</td>
                      <td className="p-3 text-gray-400 text-sm">{p.affiliate_email}</td>
                      <td className="p-3 text-right text-emerald-400 text-sm font-bold">${p.amount?.toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${statusCls(p.status)}`}>
                          {statusIcon(p.status)} {p.status}
                        </span>
                      </td>
                      <td className="p-3 text-gray-500 text-xs font-mono">{p.stripe_transfer_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-12 text-center">
                <History className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No payouts have been processed yet</p>
                <p className="text-gray-600 text-xs mt-1">Payouts are processed automatically every 2 weeks</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAffiliates;
